import { ButcherHeader } from '@components/ButcherHeader';
import { OrderNotesModal } from '@components/Modal/OrderProcessing/OrderNotesModal';
import { SideNav } from '@components/OrderCreation/OrderSideNav';
import { OrderDetailsCard } from '@components/OrderProcessing/OrderDetailsCard';
import { OrderListBreadcrumbs } from '@components/OrderProcessing/OrderListBreadcrumbs';
import { OrderStatusBadge } from '@components/OrderStatusBadge';
import { useToast } from '@contexts/Toast';
import { UseConfirmationToast } from '@contexts/ConfirmationToast';
import {
    faCircleCheck,
    faChevronDown,
    faChevronUp,
    faPen,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Axios } from '@helpers/ApiService';
import {
    errorMessage,
    Length,
    OrderProcessStatus,
    OrderProcessingMasterData,
    responseTexts,
    ServiceProcessingCrumbSteps,
    timers,
} from '@helpers/Constants';
import { ApiEndpoints, FrontendRoutes } from '@helpers/Constants/Navigation';
import {
    OrderDetailsCardInfo,
    OrderDetailsResponse,
    ServiceOrderDetails,
    ServiceTaskDetails,
} from '@helpers/Models';
import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

interface ServiceDraft {
    measurement: number;
    measurementInput: string;
    note: string;
    noteId?: number;
}

export const ServiceProcessTaskList: React.FC = () => {
    const { state } = useLocation();
    const orderId = state?.orderId;
    const { showToastMessage } = useToast();
    const { showConfirmationToast } = UseConfirmationToast();
    const navigate = useNavigate();

    const [openAccordions, setOpenAccordions] = useState<number[]>([]);
    const [orderDetails, setOrderDetails] = useState<OrderDetailsResponse>();
    const [serviceDetailsBySpecies, setServiceDetailsBySpecies] = useState<
        Record<number, ServiceOrderDetails>
    >({});
    const [serviceDrafts, setServiceDrafts] = useState<
        Record<number, ServiceDraft>
    >({});
    const [directCompleteServiceId, setDirectCompleteServiceId] = useState<
        number | null
    >(null);
    const [editingServiceId, setEditingServiceId] = useState<number | null>(null);
    const [loadingSpeciesIds, setLoadingSpeciesIds] = useState<number[]>([]);
    const [activeNotesSpeciesId, setActiveNotesSpeciesId] = useState<number | null>(
        null,
    );

    useEffect(() => {
        if (orderId) {
            fetchOrderDetails();
        } else {
            showToastMessage(errorMessage.INVALID_REQUEST, 'error');
            setTimeout(() => {
                navigate(FrontendRoutes.SERVICE_PROCESSING_LIST);
            }, timers.TOAST_AUTO_CLOSE);
        }
    }, [orderId]);

    const fetchOrderDetails = async (): Promise<void> => {
        try {
            const response = await Axios.post(ApiEndpoints.ORDER_DETAILS, {
                id: orderId,
            });
            const data: OrderDetailsResponse = response.data.data;
            setOrderDetails(data);

            if (data?.species?.length > 0) {
                const speciesWithServices = data.species.filter(
                    (species) => species.services && species.services.length > 0,
                );
                setOpenAccordions(speciesWithServices.length > 0 ? [0] : []);

                await Promise.allSettled(
                    speciesWithServices.map((species) =>
                        fetchServiceTaskDetailsBySpecies(species.speciesId, false),
                    ),
                );
            }
        } catch (error) {
            showToastMessage(errorMessage.FETCH_ERROR, 'error');
        }
    };

    const fetchServiceTaskDetailsBySpecies = async (
        orderSpeciesId: number,
        showErrorToast = true,
    ): Promise<void> => {
        setLoadingSpeciesIds((prev) =>
            prev.includes(orderSpeciesId) ? prev : [...prev, orderSpeciesId],
        );

        try {
            const response = await Axios.post(ApiEndpoints.SERVICE_PROCESS_TASK_DETAILS, {
                orderSpeciesId,
            });
            const details = response.data.data?.[0] as ServiceOrderDetails;

            if (details) {
                setServiceDetailsBySpecies((prev) => ({
                    ...prev,
                    [orderSpeciesId]: details,
                }));
            }
        } catch (error) {
            if (showErrorToast) {
                showToastMessage(errorMessage.FETCH_ERROR, 'error');
            }
        } finally {
            setLoadingSpeciesIds((prev) => prev.filter((id) => id !== orderSpeciesId));
        }
    };

    const toggleAccordion = (index: number): void => {
        const isOpening = !openAccordions.includes(index);
        setOpenAccordions((prev) => (prev.includes(index) ? [] : [index]));

        if (!isOpening) {
            return;
        }

        const species = speciesWithServices[index];
        if (!species) {
            return;
        }

        if (!serviceDetailsBySpecies[species.speciesId]) {
            fetchServiceTaskDetailsBySpecies(species.speciesId);
        }
    };

    const buildDefaultDraft = (
        service: ServiceTaskDetails,
        defaultMeasurement?: number,
    ): ServiceDraft => {
        const measurementValue =
            defaultMeasurement ?? service.serviceProcessDetail?.finalMeasurement ?? 0;

        return {
            measurement: measurementValue,
            measurementInput: String(measurementValue),
            note: service.serviceNotes?.[0]?.note ?? '',
            noteId: service.serviceNotes?.[0]?.id,
        };
    };

    const ensureDraft = (
        service: ServiceTaskDetails,
        defaultMeasurement?: number,
    ): void => {
        setServiceDrafts((prev) => {
            if (prev[service.orderServiceId]) {
                return prev;
            }
            return {
                ...prev,
                [service.orderServiceId]: buildDefaultDraft(service, defaultMeasurement),
            };
        });
    };

    const updateDraft = (
        serviceId: number,
        values: Partial<ServiceDraft>,
    ): void => {
        setServiceDrafts((prev) => {
            const existing = prev[serviceId] ?? {
                measurement: 0,
                measurementInput: '0',
                note: '',
            };
            return {
                ...prev,
                [serviceId]: {
                    ...existing,
                    ...values,
                },
            };
        });
    };

    const handleStartProcessing = async (
        speciesId: number,
        service: ServiceTaskDetails,
    ): Promise<void> => {
        try {
            await Axios.post(ApiEndpoints.SERVICE_PROCESS_WORKFLOW, {
                orderServiceId: service.orderServiceId,
                status: OrderProcessStatus.INPROGRESS,
            });

            showToastMessage(responseTexts.SERVICE_WORKFLOW_START_SUCCESS, 'success');
            await fetchServiceTaskDetailsBySpecies(speciesId);
        } catch (error) {
            showToastMessage(errorMessage.ERROR_UPDATING_SERVICE_WORKFLOW, 'error');
        }
    };

    const upsertServiceNote = async (
        speciesDetails: ServiceOrderDetails,
        service: ServiceTaskDetails,
        draft: ServiceDraft,
    ): Promise<void> => {
        const note = draft.note.trim();
        if (!note) {
            return;
        }

        const response = await Axios.post(ApiEndpoints.SERVICE_PROCESS_UPSERT_NOTES, {
            orderId: speciesDetails.orderId,
            orderSpeciesId: speciesDetails.orderSpeciesId,
            orderServiceId: service.orderServiceId,
            note,
            ...(draft.noteId ? { id: draft.noteId } : {}),
        });

        const updatedNote = response?.data?.data;
        if (updatedNote?.id) {
            updateDraft(service.orderServiceId, { noteId: updatedNote.id });
        }
    };

    const handleResetService = async (service: ServiceTaskDetails): Promise<void> => {
        const confirmReset = async (): Promise<void> => {
            try {
                await Axios.post(ApiEndpoints.SERVICE_PROCESS_RESTORE, {
                    orderServiceId: service.orderServiceId,
                });

                showToastMessage('Service reset successfully.', 'success');
                await fetchOrderDetails();
            } catch (error) {
                showToastMessage('Failed to reset service.', 'error');
            }
        };

        showConfirmationToast(
            'The service data will go to its original state losing all the current changes. Do you wish to continue',
            'Reset Service',
            'delete',
            confirmReset,
            'Reset',
            undefined,
            'Cancel',
        );
    };

    const handleCompleteProcessing = async (
        speciesId: number,
        speciesDetails: ServiceOrderDetails,
        service: ServiceTaskDetails,
        defaultMeasurement?: number,
    ): Promise<void> => {
        const draft =
            serviceDrafts[service.orderServiceId] ??
            buildDefaultDraft(service, defaultMeasurement);
        const finalMeasurement = draft.measurement ?? 0;
        const hasMeasurementInput = !service.serviceFlatFeeFlag;

        if (hasMeasurementInput && finalMeasurement <= 0) {
            showToastMessage('Measurement cannot be 0', 'error');
            return;
        }

        try {
            await Axios.post(ApiEndpoints.SERVICE_PROCESS_WORKFLOW, {
                orderServiceId: service.orderServiceId,
                status: OrderProcessStatus.COMPLETED,
                finalMeasurement,
            });

            await upsertServiceNote(speciesDetails, service, draft);

            showToastMessage(responseTexts.SERVICE_WORKFLOW_FINISH_SUCCESS, 'success');
            setDirectCompleteServiceId(null);
            setEditingServiceId(null);
            await fetchServiceTaskDetailsBySpecies(speciesId);
        } catch (error) {
            showToastMessage(errorMessage.ERROR_UPDATING_SERVICE_WORKFLOW, 'error');
        }
    };

    const handleCancelInlineEdit = (service: ServiceTaskDetails): void => {
        const defaultMeasurement = service.serviceProcessDetail?.finalMeasurement ?? 0;
        const draft = buildDefaultDraft(service, defaultMeasurement);
        updateDraft(service.orderServiceId, draft);
        setDirectCompleteServiceId(null);
        setEditingServiceId(null);
    };

    const handleIncrement = (
        service: ServiceTaskDetails,
        defaultMeasurement?: number,
    ): void => {
        const current =
            serviceDrafts[service.orderServiceId]?.measurement ??
            service.serviceProcessDetail?.finalMeasurement ??
            defaultMeasurement ??
            0;

        const updatedMeasurement = current + 0.5;

        updateDraft(service.orderServiceId, {
            measurement: updatedMeasurement,
            measurementInput: String(updatedMeasurement),
        });
    };

    const handleDecrement = (
        service: ServiceTaskDetails,
        defaultMeasurement?: number,
    ): void => {
        const current =
            serviceDrafts[service.orderServiceId]?.measurement ??
            service.serviceProcessDetail?.finalMeasurement ??
            defaultMeasurement ??
            0;

        const updatedMeasurement = current > 0 ? current - 0.5 : 0;

        updateDraft(service.orderServiceId, {
            measurement: updatedMeasurement,
            measurementInput: String(updatedMeasurement),
        });
    };

    // Helper to pluralize unit when value != 1 and unit is 'foot'
    const getDisplayUnit = (value: number, unit: string): string => {
        if (unit === 'foot' && value !== 1) {
            return 'feet';
        }
        return unit;
    };

    const orderDetailsCardInfo = {
        orderNumber: orderDetails?.orderInfo.orderNumber,
        createdByUser: orderDetails?.orderInfo.createdByUser,
        roles: orderDetails?.orderInfo.roles,
        customerName: orderDetails?.orderInfo.customerName,
        orderDate: orderDetails?.orderInfo.orderDate,
    } as OrderDetailsCardInfo;

    const BreadCrumbsProps = {
        steps: ServiceProcessingCrumbSteps,
        activeItem: 'Service Processing -',
        orderNumber: orderDetails?.orderInfo.orderNumber,
        type: OrderProcessingMasterData.SERVICE_PROCESSING,
    };

    const speciesWithServices =
        orderDetails?.species?.filter(
            (species) => species.services && species.services.length > 0,
        ) ?? [];

    const activeNotesSpecies =
        activeNotesSpeciesId === null
            ? undefined
            : orderDetails?.species.find(
                (species) => species.speciesId === activeNotesSpeciesId,
            );

    return (
        <div className="flex h-screen w-screen gap-1">
            <SideNav active={4} />
            <div className="flex-1 p-1 overflow-auto relative">
                <ButcherHeader />
                <OrderListBreadcrumbs orderInfo={BreadCrumbsProps} />
                <div className="p-3">
                    <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2 text-gray-700">
                            <button
                                type="button"
                                onClick={() => window.history.back()}
                                className="p-1"
                            >
                                <svg
                                    width="16"
                                    height="16"
                                    viewBox="0 0 16 16"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <path
                                        d="M10.7071 12.7071C10.3166 13.0976 9.68342 13.0976 9.29289 12.7071L4.29289 7.70711C3.90237 7.31658 3.90237 6.68342 4.29289 6.29289L9.29289 1.29289C9.68342 0.902369 10.3166 0.902369 10.7071 1.29289C11.0976 1.68342 11.0976 2.31658 10.7071 2.70711L6.41421 7L10.7071 11.2929C11.0976 11.6834 11.0976 12.3166 10.7071 12.7071Z"
                                        fill="currentColor"
                                    />
                                </svg>
                            </button>
                            <h2 className="text-lg font-semibold text-gray-900">
                                Service Processing - {orderDetails?.orderInfo.orderNumber}
                            </h2>
                        </div>
                    </div>

                    {orderDetails && (
                        <OrderDetailsCard orderDetailsCardInfo={orderDetailsCardInfo} />
                    )}

                    <h1 className="font-semibold text-xl mt-3">Service Processing Tasks</h1>

                    {speciesWithServices.length === 0 ? (
                        <div className="text-center text-gray-500 mt-8">
                            No species with services available for processing.
                        </div>
                    ) : (
                        speciesWithServices.map((speciesInfo, index) => (
                            <div key={speciesInfo.speciesId}>
                                <div className="border rounded-md shadow-sm mb-3 mt-4">
                                    <button
                                        type="button"
                                        onClick={() => toggleAccordion(index)}
                                        className="w-full flex justify-between items-center px-4 py-3 font-semibold text-lg focus:outline-primary"
                                    >
                                        <span className="flex items-center gap-2">
                                            <span className="text-xl font-bold text-gray-900">
                                                {speciesInfo.speciesTag}
                                                {speciesInfo.checkIn && speciesInfo.checkIn.inches && (
                                                    <>
                                                        {' '}({speciesInfo.checkIn.feet} {Length.FEET}{' '}
                                                        {speciesInfo.checkIn.inches} {Length.INCHES})
                                                    </>
                                                )}
                                            </span>
                                            <button
                                                type="button"
                                                onClick={(event) => {
                                                    event.preventDefault();
                                                    event.stopPropagation();
                                                    setActiveNotesSpeciesId(speciesInfo.speciesId);
                                                }}
                                                className="text-primary p-1 text-sm font-bold"
                                            >
                                                View Notes
                                            </button>
                                        </span>
                                        <div className="flex items-center gap-4">
                                            <FontAwesomeIcon
                                                className="text-[#000A16]"
                                                icon={
                                                    openAccordions.includes(index) ? faChevronUp : faChevronDown
                                                }
                                            />
                                        </div>
                                    </button>

                                    {openAccordions.includes(index) && (
                                        <div>
                                            {loadingSpeciesIds.includes(speciesInfo.speciesId) ? (
                                                <div className="p-3 text-gray-500">Loading services...</div>
                                            ) : serviceDetailsBySpecies[speciesInfo.speciesId]?.services
                                                ?.length ? (
                                                <div>
                                                    {serviceDetailsBySpecies[speciesInfo.speciesId].services.map(
                                                        (service) => {
                                                            // Compute converted length for this service (if unit is inch or foot)
                                                            const feet = speciesInfo.checkIn?.feet || 0;
                                                            const inches = speciesInfo.checkIn?.inches || 0;
                                                            const totalInches = feet * 12 + inches;
                                                            let convertedLength = 0;
                                                            if (service.serviceUnit === 'inch') {
                                                                convertedLength = Math.round(totalInches * 100) / 100;
                                                            } else if (service.serviceUnit === 'foot') {
                                                                convertedLength = Math.round((totalInches / 12) * 100) / 100;
                                                            }
                                                            // Use existing final measurement if present, else the converted length
                                                            const existingMeasurement = service.serviceProcessDetail?.finalMeasurement;
                                                            const defaultMeasurement = (existingMeasurement !== undefined && existingMeasurement !== null)
                                                                ? existingMeasurement
                                                                : convertedLength;

                                                            const serviceStatus = Number(service.orderServiceStatus ?? 0);
                                                            const isInitial = serviceStatus === OrderProcessStatus.INITIAL;
                                                            const isInProgress = serviceStatus === OrderProcessStatus.INPROGRESS;
                                                            const isCompleted = serviceStatus >= OrderProcessStatus.COMPLETED;
                                                            const isFinalized = serviceStatus > OrderProcessStatus.COMPLETED;
                                                            const isDirectComplete = directCompleteServiceId === service.orderServiceId;
                                                            const isEditingCompleted = editingServiceId === service.orderServiceId;
                                                            const showInlineEditor = isInProgress || isDirectComplete || isEditingCompleted;

                                                            // Get the draft; if none, create one with the defaultMeasurement
                                                            const draft = serviceDrafts[service.orderServiceId] ?? {
                                                                measurement: defaultMeasurement,
                                                                note: service.serviceNotes?.[0]?.note ?? '',
                                                                noteId: service.serviceNotes?.[0]?.id,
                                                            };

                                                            return (
                                                                <div
                                                                    key={service.orderServiceId}
                                                                    className={`rounded-md border shadow-md mt-4 mx-3 ${showInlineEditor ? 'bg-quaternary' : 'bg-[#FFFFFF]'
                                                                        }`}
                                                                >
                                                                    <div>
                                                                        <div className="p-3 rounded-md flex items-center gap-4">
                                                                            <img
                                                                                src={speciesInfo.imageUrl}
                                                                                alt={speciesInfo.name}
                                                                                className="w-20 h-20 object-cover rounded-md"
                                                                            />
                                                                            <div className="flex-1 space-y-2">
                                                                                <div className="flex justify-between items-center">
                                                                                    <p className="text-lg">{service.serviceName}</p>
                                                                                    <div className="flex items-center gap-2">
                                                                                        {!isEditingCompleted && isCompleted && !isFinalized && (
                                                                                            <>
                                                                                                <button
                                                                                                    type="button"
                                                                                                    onClick={() => handleResetService(service)}
                                                                                                    className="font-bold text-red-600 flex items-center gap-2 hover:text-red-700"
                                                                                                >
                                                                                                    Reset
                                                                                                </button>
                                                                                                <span className="text-[#00000033] text-2xl">|</span>
                                                                                            </>
                                                                                        )}
                                                                                        {isCompleted && !isEditingCompleted && !isFinalized && (
                                                                                            <button
                                                                                                type="button"
                                                                                                onClick={() => {
                                                                                                    const editDefault = service.serviceProcessDetail?.finalMeasurement ?? convertedLength;
                                                                                                    ensureDraft(service, editDefault);
                                                                                                    setEditingServiceId(service.orderServiceId);
                                                                                                }}
                                                                                                className="font-bold text-primary flex items-center gap-2"
                                                                                            >
                                                                                                <FontAwesomeIcon icon={faPen} />
                                                                                                Edit
                                                                                            </button>
                                                                                        )}
                                                                                        <OrderStatusBadge
                                                                                            status={service.orderServiceStatus}
                                                                                            type="serviceStatus"
                                                                                        />
                                                                                    </div>
                                                                                </div>

                                                                                <div className="bg-[#F5F5F5] p-2 rounded-md text-sm inline-block w-full">
                                                                                    {service.serviceFlatFeeFlag
                                                                                        ? 'Flat Fee'
                                                                                        : 'Final Price to be calculated after processing'}
                                                                                </div>

                                                                                {showInlineEditor && !service.serviceFlatFeeFlag && (
                                                                                    <div className="bg-[#FFFFFF] p-2 rounded-md flex flex-col sm:flex-row sm:items-center sm:space-x-4 space-y-2 sm:space-y-0">
                                                                                        <span>Measurements after processing</span>
                                                                                        <div className="flex flex-col">
                                                                                            <div className="flex items-center">
                                                                                                <button
                                                                                                    type="button"
                                                                                                    onClick={() =>
                                                                                                        handleDecrement(
                                                                                                            service,
                                                                                                            defaultMeasurement,
                                                                                                        )
                                                                                                    }
                                                                                                    className="w-12 h-8 flex items-center justify-center border rounded-l text-white text-lg bg-primary hover:bg-primary"
                                                                                                >
                                                                                                    -
                                                                                                </button>
                                                                                                <input
                                                                                                    type="number"
                                                                                                    step="0.5"
                                                                                                    value={draft.measurementInput ?? String(draft.measurement ?? 0)}
                                                                                                    onChange={(e) => {
                                                                                                        const rawValue = e.target.value;
                                                                                                        const val = parseFloat(rawValue);
                                                                                                        updateDraft(service.orderServiceId, {
                                                                                                            measurementInput: rawValue,
                                                                                                            measurement: isNaN(val) ? 0 : val,
                                                                                                        });
                                                                                                    }}
                                                                                                    onFocus={(e) => e.target.select()}
                                                                                                    className="w-20 h-8 text-center border-t border-b focus:outline-primary"
                                                                                                />
                                                                                                <button
                                                                                                    type="button"
                                                                                                    onClick={() =>
                                                                                                        handleIncrement(
                                                                                                            service,
                                                                                                            defaultMeasurement,
                                                                                                        )
                                                                                                    }
                                                                                                    className="w-12 h-8 flex items-center justify-center border rounded-r text-white text-lg bg-primary hover:bg-primary"
                                                                                                >
                                                                                                    +
                                                                                                </button>
                                                                                                <span className="ml-2">{getDisplayUnit(draft.measurement ?? 0, service.serviceUnit)}</span>
                                                                                            </div>
                                                                                        </div>
                                                                                    </div>
                                                                                )}

                                                                                {showInlineEditor && (
                                                                                    <div className="bg-[#FFFFFF] p-2 rounded-md">
                                                                                        <label className="text-sm font-medium text-gray-700 block mb-1">
                                                                                            Service notes
                                                                                        </label>
                                                                                        <textarea
                                                                                            value={draft.note}
                                                                                            onChange={(e) =>
                                                                                                updateDraft(service.orderServiceId, {
                                                                                                    note: e.target.value,
                                                                                                })
                                                                                            }
                                                                                            className="w-full border rounded p-2 min-h-[90px] resize-none focus:outline-primary"
                                                                                            placeholder="Enter service notes..."
                                                                                        />
                                                                                    </div>
                                                                                )}

                                                                                {isCompleted && !isEditingCompleted && (
                                                                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                                                                        {!service.serviceFlatFeeFlag && (
                                                                                            <div className="bg-[#e1fce9] border border-[#2D5B4A33] rounded-md p-3">
                                                                                                <p className="text-xs font-semibold uppercase tracking-wide text-[#2D5B4A]">
                                                                                                    Final Measurement
                                                                                                </p>
                                                                                                <p className="text-sm font-semibold text-[#2D5B4A] mt-1">
                                                                                                    {service.serviceProcessDetail?.finalMeasurement
                                                                                                        ? `${service.serviceProcessDetail.finalMeasurement} ${getDisplayUnit(service.serviceProcessDetail.finalMeasurement, service.serviceUnit)}`
                                                                                                        : 'Final Price to be calculated after processing'}
                                                                                                </p>
                                                                                            </div>
                                                                                        )}
                                                                                        <div
                                                                                            className={`border border-[#2D5B4A33] rounded-md p-3 ${service.serviceFlatFeeFlag
                                                                                                ? 'md:col-span-2'
                                                                                                : ''
                                                                                                }`}
                                                                                        >
                                                                                            <p className="text-xs font-semibold uppercase tracking-wide text-[#2D5B4A]">
                                                                                                Service Notes
                                                                                            </p>
                                                                                            <p className="text-sm text-[#2D5B4A] mt-1 break-words whitespace-pre-wrap leading-6">
                                                                                                {service.serviceNotes?.[0]?.note ||
                                                                                                    errorMessage.NO_NOTES_FOUND}
                                                                                            </p>
                                                                                        </div>
                                                                                    </div>
                                                                                )}
                                                                            </div>
                                                                        </div>

                                                                        {service.serviceProcessDetail?.serviceStartDate && (
                                                                            <div className="px-3 py-1 flex justify-between">
                                                                                <span className="text-gray-600 font-semibold">
                                                                                    Start date:{' '}
                                                                                    {new Date(
                                                                                        service.serviceProcessDetail.serviceStartDate,
                                                                                    ).toLocaleString('en-US')}
                                                                                </span>
                                                                                {service.serviceProcessDetail?.serviceEndDate && (
                                                                                    <span className="text-gray-600 font-semibold">
                                                                                        End date:{' '}
                                                                                        {new Date(
                                                                                            service.serviceProcessDetail.serviceEndDate,
                                                                                        ).toLocaleString('en-US')}
                                                                                    </span>
                                                                                )}
                                                                            </div>
                                                                        )}

                                                                        <div className="p-3">
                                                                            {isInitial && !isDirectComplete ? (
                                                                                <div className="flex flex-wrap justify-end gap-2 mt-1">
                                                                                    <button
                                                                                        type="button"
                                                                                        onClick={() =>
                                                                                            handleStartProcessing(
                                                                                                speciesInfo.speciesId,
                                                                                                service,
                                                                                            )
                                                                                        }
                                                                                        className="w-full sm:w-auto sm:min-w-[170px] sm:max-w-[200px] px-4 py-1.5 border border-transparent rounded-md text-sm font-medium text-white bg-primary hover:bg-dark-primary focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary flex items-center justify-center"
                                                                                    >
                                                                                        Start Processing
                                                                                    </button>
                                                                                    <button
                                                                                        type="button"
                                                                                        onClick={() => {
                                                                                            const completeDefault = service.serviceProcessDetail?.finalMeasurement ?? convertedLength;
                                                                                            ensureDraft(service, completeDefault);
                                                                                            setDirectCompleteServiceId(service.orderServiceId);
                                                                                        }}
                                                                                        className="w-full sm:w-auto sm:min-w-[170px] sm:max-w-[200px] px-4 py-1.5 border border-primary text-sm font-medium text-primary bg-white hover:bg-primary hover:text-white focus:outline-none rounded-md focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                                                                                    >
                                                                                        Complete Processing
                                                                                    </button>
                                                                                </div>
                                                                            ) : isInProgress ? (
                                                                                <button
                                                                                    type="button"
                                                                                    onClick={() =>
                                                                                        handleCompleteProcessing(
                                                                                            speciesInfo.speciesId,
                                                                                            serviceDetailsBySpecies[
                                                                                            speciesInfo.speciesId
                                                                                            ],
                                                                                            service,
                                                                                            defaultMeasurement,
                                                                                        )
                                                                                    }
                                                                                    className="w-full sm:w-auto sm:min-w-[170px] sm:max-w-[200px] sm:ml-auto px-4 py-1.5 border border-transparent rounded-sm text-sm font-medium text-white bg-primary hover:bg-dark-primary focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary flex items-center justify-center"
                                                                                >
                                                                                    Complete Processing
                                                                                </button>
                                                                            ) : isDirectComplete ? (
                                                                                <div className="flex flex-wrap justify-end gap-2 mt-3">
                                                                                    <button
                                                                                        onClick={() => handleCancelInlineEdit(service)}
                                                                                        type="button"
                                                                                        className="w-full sm:w-auto sm:min-w-[170px] sm:max-w-[200px] px-4 py-1.5 border border-primary rounded-md text-sm font-medium text-primary bg-white hover:bg-primary hover:text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                                                                                    >
                                                                                        Cancel
                                                                                    </button>
                                                                                    <button
                                                                                        type="button"
                                                                                        onClick={() =>
                                                                                            handleCompleteProcessing(
                                                                                                speciesInfo.speciesId,
                                                                                                serviceDetailsBySpecies[
                                                                                                speciesInfo.speciesId
                                                                                                ],
                                                                                                service,
                                                                                                defaultMeasurement,
                                                                                            )
                                                                                        }
                                                                                        className="w-full sm:w-auto sm:min-w-[170px] sm:max-w-[200px] px-4 py-1.5 border border-transparent rounded-md text-sm font-medium text-white bg-primary hover:bg-dark-primary focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary flex items-center justify-center"
                                                                                    >
                                                                                        Complete Processing
                                                                                    </button>
                                                                                </div>
                                                                            ) : isEditingCompleted ? (
                                                                                <div className="flex flex-wrap justify-end gap-2 mt-3">
                                                                                    <button
                                                                                        onClick={() => handleCancelInlineEdit(service)}
                                                                                        type="button"
                                                                                        className="w-full sm:w-auto sm:min-w-[170px] sm:max-w-[200px] px-4 py-1.5 border border-primary rounded-md text-sm font-medium text-primary bg-white hover:bg-primary hover:text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                                                                                    >
                                                                                        Cancel
                                                                                    </button>
                                                                                    <button
                                                                                        type="button"
                                                                                        onClick={() =>
                                                                                            handleCompleteProcessing(
                                                                                                speciesInfo.speciesId,
                                                                                                serviceDetailsBySpecies[
                                                                                                speciesInfo.speciesId
                                                                                                ],
                                                                                                service,
                                                                                                defaultMeasurement,
                                                                                            )
                                                                                        }
                                                                                        className="w-full sm:w-auto sm:min-w-[170px] sm:max-w-[200px] px-4 py-1.5 border border-transparent rounded-md text-sm font-medium text-white bg-primary hover:bg-dark-primary focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary flex items-center justify-center"
                                                                                    >
                                                                                        Save Changes
                                                                                    </button>
                                                                                </div>
                                                                            ) : (
                                                                                <span className="inline-block w-full border bg-[#CCEECC] p-2 text-[#2D5B4A] border-[#2D5B4A] text-center font-semibold rounded-md">
                                                                                    Service Completed
                                                                                    <FontAwesomeIcon
                                                                                        icon={faCircleCheck}
                                                                                        className="ml-2 text-primary"
                                                                                    />
                                                                                </span>
                                                                            )}
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            );
                                                        },
                                                    )}
                                                </div>
                                            ) : (
                                                <div className="p-3 text-gray-500">No services available.</div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>
                        ))
                    )}

                    {activeNotesSpecies && (
                        <OrderNotesModal
                            speciesName={activeNotesSpecies.speciesTag ?? ''}
                            onClose={() => setActiveNotesSpeciesId(null)}
                            checkInNotes={
                                activeNotesSpecies.checkIn?.notes || errorMessage.NO_NOTES_FOUND
                            }
                            AdditionalNotes={
                                activeNotesSpecies.notes || errorMessage.NO_NOTES_FOUND
                            }
                        />
                    )}
                </div>
            </div>
        </div>
    );
};